import axios from 'axios';

class NetworkService {
    constructor() {
        // Defaults from environment or build config
        this.localIp = import.meta.env.VITE_LOCAL_API_IP || '';
        this.publicDomain = import.meta.env.VITE_PUBLIC_API_DOMAIN || '';
        
        // Active configuration
        this.activeBaseUrl = null;
        this.isLocal = false;
        
        // Interval for background checking
        this.checkInterval = null;
    }

    /**
     * Determines the fastest available network path (LAN vs Internet).
     * @returns {Promise<string>} The active base URL
     */
    async detectOptimalNetwork() {
        const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
        const publicUrl = this.publicDomain ? `https://${this.publicDomain}/api/v1` : `${currentOrigin}/api/v1`;

        // If local IP is not set, default directly to current origin / relative API
        if (!this.localIp) {
            this.activeBaseUrl = publicUrl;
            this.isLocal = false;
            return this.activeBaseUrl;
        }

        // If we are currently running on localhost (dev mode), just use relative paths
        if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
            this.activeBaseUrl = '/api/v1';
            this.isLocal = true;
            return this.activeBaseUrl;
        }

        const localUrl = `http://${this.localIp}/api/v1`;

        try {
            // Ping the local health endpoint with a short timeout
            const startTime = Date.now();
            await axios.get(`${localUrl}/health`, { 
                timeout: 1000 // 1 second max for LAN ping
            });
            
            const duration = Date.now() - startTime;
            console.log(`[NetworkService] LAN connection successful (${duration}ms)`);
            
            this.activeBaseUrl = localUrl;
            this.isLocal = true;

        } catch (error) {
            console.log('[NetworkService] LAN connection failed. Falling back to Internet.');
            this.activeBaseUrl = publicUrl;
            this.isLocal = false;
        }

        return this.activeBaseUrl;
    }

    /**
     * Start polling in the background to detect network changes.
     */
    startBackgroundPolling(intervalMs = 60000) {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
        }

        this.checkInterval = setInterval(async () => {
            const previousIsLocal = this.isLocal;
            await this.detectOptimalNetwork();
            
            if (previousIsLocal !== this.isLocal) {
                console.log(`[NetworkService] Network switched! Now using ${this.isLocal ? 'LAN' : 'Internet'}`);
            }
        }, intervalMs);
    }

    getActiveBaseUrl() {
        return this.activeBaseUrl || '/api/v1'; // fallback to relative
    }

    isLocalNetwork() {
        return this.isLocal;
    }
}

export default new NetworkService();
